import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import { moneyRequestsTableColumns } from "../../../data/FormColumns/Fleet/moneyRequestsTableColumns";
import axios from "axios";

import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchAllRequests = async (
  token: string | null | undefined,
  month?: number,
  year?: number,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllRequests`, {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      month,
      year,
    },
  });
  return data;
};

const AllRequestsTable: React.FC = () => {
  const { token, role } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1,
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["allRequests", selectedMonth, selectedYear, showAll],
    queryFn: () =>
      fetchAllRequests(
        token,
        showAll ? undefined : selectedMonth,
        showAll ? undefined : selectedYear,
      ),
    enabled: role?.includes("Accounting"),
  });

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((request: any) =>
      [
        request.driverName,
        request.unitNumber,
        request.nameRequestedBy,
        request.dateRequested,
        request.amount,
        request.requestFor,
        request.moneyRequestType,
        request.paymentMethod,
        request.description,
        request.issuedFor,
        request.referenceId,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  return (
    <Box className="money-requests-table-container" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        <Grid container spacing={2} sx={{ width: "auto" }}>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                disabled={showAll}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                disabled={showAll}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={(e) => {
                    setShowAll(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedMonth(new Date().getMonth() + 1);
                      setSelectedYear(new Date().getFullYear());
                    }
                  }}
                />
              }
              label="Show All Requests"
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by relevant fields"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>
      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {error.message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="allRequests"
          rows={filteredData || []}
          columns={moneyRequestsTableColumns}
          compactViewConfig={{
            primaryField: "driverName",
            secondaryFields: ["unitNumber", "dateRequested"],
            detailFields: [
              "amount",
              "moneyRequestType",
              "paymentMethod",
              "nameRequestedBy",
              "referenceId",
              "issuedFor",
              "description",
            ],
            actionFields: [
              "downloadInvoice",
              "downloadReceipt",
              "update",
              "delete",
            ],
            statusField: "status",
          }}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default AllRequestsTable;
